// The Design_System stylesheet first, then this application's theme.
import '@hurdl/design-system/styles.css';
import './theme.css';

import {
  AuthenticationProvider,
  createAuthenticationStore,
} from '@hurdl/authentication/frontend';
import { ThemeSurface, type ThemeMode } from '@hurdl/design-system';
import { RouterProvider } from '@tanstack/react-router';
import {
  StrictMode,
  useEffect,
  useMemo,
  useState,
  type ReactElement,
} from 'react';
import { createRoot } from 'react-dom/client';

import { installAuthenticationGuard } from './pages/auth-guard.js';
import { SessionMenuOperationsProvider } from './pages/menus-wiring.js';
import { router } from './routes.js';
import { APPLICATION_THEME } from './theme.js';

/** The media query the panel follows for its light/dark mode. */
const DARK_SCHEME_QUERY = '(prefers-color-scheme: dark)';

/**
 * The Operator's own light/dark preference, tracked live.
 *
 * `ThemeSurface` is the platform's one theme switcher, and mode is one of its two axes;
 * what it does not do is *decide* the mode — that is the application's call, and this
 * panel's call is to follow the operating system, the way a desktop tool does. The
 * media query is read synchronously for the first paint (so a dark OS does not flash
 * light) and then watched, so toggling the OS between light and dark repaints the panel
 * without a reload. `light` on the server and where `matchMedia` is absent, which
 * matches `ThemeSurface`'s own default.
 */
function usePreferredMode(): ThemeMode {
  const [mode, setMode] = useState<ThemeMode>(() => {
    if (typeof window === 'undefined' || !('matchMedia' in window)) {
      return 'light';
    }

    return window.matchMedia(DARK_SCHEME_QUERY).matches ? 'dark' : 'light';
  });

  useEffect(() => {
    if (typeof window === 'undefined' || !('matchMedia' in window)) {
      return;
    }

    const query = window.matchMedia(DARK_SCHEME_QUERY);
    const update = (): void => {
      setMode(query.matches ? 'dark' : 'light');
    };

    update();
    query.addEventListener('change', update);

    return () => {
      query.removeEventListener('change', update);
    };
  }, []);

  return mode;
}

/**
 * The Admin_Panel's root: the application's theme surface, following the OS light/dark
 * preference, rendered full-bleed so the panel's own shell owns every edge.
 *
 * `ThemeSurface` paints the page and defaults to a comfortable padding and
 * `min-h-screen`; the panel overrides both — `p-0` because the shell, not the surface,
 * decides where the chrome sits (there is no zero step on the spacing scale, so this is
 * Tailwind's own zero rather than a token), `fill={false}` to drop the `min-h-screen`,
 * and `h-screen overflow-hidden` because the shell is a fixed frame whose content
 * column does its own scrolling, so there is no page-level scrollbar and no gutter
 * ringing the application in either mode. The theme and mode still flow through
 * `ThemeSurface` alone (ADR 0005); only the box it draws is the panel's to shape,
 * through the `className` it composes last.
 */
function Root(): ReactElement {
  const mode = usePreferredMode();

  // The authentication store is built here, rather than left to the provider, so this
  // module holds the one reference the route guard needs: the guard reads the store's
  // snapshot and the router's location to send an un-signed-in operator to the login page
  // and a signed-in one off it. It is wired here, outside the component tree, because the
  // shell (`layout.tsx`) is mounted by the Cosmos Fixtures with no provider above it, so a
  // guard that lived there and read `useAuthentication` would break the appearance check.
  // The store's `baseUrl` is the provider's own default, `/authentication` — the path the
  // backend mounts the capability's router at and the dev server now proxies — stated once
  // and handed to the provider so the two cannot diverge. A relative path, so no address
  // literal reaches this module (invariant 2).
  const store = useMemo(
    () => createAuthenticationStore({ baseUrl: '/authentication' }),
    [],
  );

  useEffect(() => {
    // Inert when the login is open (the frictionless in-memory and `--db local` runs):
    // `installAuthenticationGuard` installs nothing and returns a no-op teardown there.
    // Where the login is required it subscribes to the store and the router, waits out
    // session recovery, and redirects on the settled status. Removed on unmount so a
    // development remount does not stack subscriptions.
    return installAuthenticationGuard(store, router);
  }, [store]);

  return (
    <ThemeSurface
      theme={APPLICATION_THEME}
      mode={mode}
      fill={false}
      className="p-0 h-screen overflow-hidden"
    >
      {/*
        The Authentication_Capability's provider sits above the whole tree: the login
        page drives its passwordless ceremony through it, the account-admin pages compose
        its operations through it, and the review pages read their operator from its
        snapshot. It is handed the store built above (whose `baseUrl` is `/authentication`,
        the path the panel's backend mounts the capability's router at — a relative path,
        so no address literal reaches this module), and recovers the session from the
        httpOnly cookie on mount. The session operations provider reads the signed-in
        operator from it and attributes the review writes to that account rather than a
        hardcoded one.
      */}
      <AuthenticationProvider store={store}>
        <SessionMenuOperationsProvider>
          <RouterProvider router={router} />
        </SessionMenuOperationsProvider>
      </AuthenticationProvider>
    </ThemeSurface>
  );
}

const container = document.getElementById('root');

if (container === null) {
  throw new Error(
    "index.html supplied no element with the id 'root', so there is nothing to mount into",
  );
}

createRoot(container).render(
  <StrictMode>
    <Root />
  </StrictMode>,
);
